import { useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Bug,
  ExternalLink,
  Paperclip,
  RefreshCw,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { AttachmentPreviewGrid } from '@/components/shared/AttachmentPreviewGrid'
import { FileUploadDropzone } from '@/components/shared/FileUploadDropzone'
import { PaymentStatusBadge } from '@/components/shared/StatusBadges'
import { UpdatePaymentStatusDialog } from '@/components/payments/UpdatePaymentStatusDialog'
import { useJob } from '@/hooks/useJobs'
import { usePayment, useUpdatePaymentStatus } from '@/hooks/usePayments'
import { usePermissions } from '@/hooks/usePermissions'
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext'
import { paymentsApi } from '@/services/payments'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { formatCurrency, formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { AttachmentRef } from '@/components/shared'
import {
  isPaymentProofFileSupported,
  isPaymentProofFileTooLarge,
  PAYMENT_PROOF_ACCEPTED_EXT,
} from '@/components/payments/paymentProofPreview'
import {
  PAYMENT_FAILURE_REASON_LABELS,
  PAYMENT_METHOD_LABELS,
  type PaymentMethod,
  type PaymentStatus,
} from '@/types'

const PAYMENT_AMOUNT_THEMES: Record<
  PaymentStatus,
  {
    backgroundColor: string
    borderColor: string
    labelColor: string
    amountColor: string
    metaColor: string
  }
> = {
  pending: {
    backgroundColor: '#FFF1EA',
    borderColor: '#F7D7CB',
    labelColor: '#5A5A5A',
    amountColor: '#FF5A1F',
    metaColor: '#FF5A1F',
  },
  paid: {
    backgroundColor: '#ECFDF5',
    borderColor: '#A7F3D0',
    labelColor: '#5A5A5A',
    amountColor: '#047857',
    metaColor: '#047857',
  },
  failed: {
    backgroundColor: '#FDE9E8',
    borderColor: '#FCA5A5',
    labelColor: '#5A5A5A',
    amountColor: '#991B1B',
    metaColor: '#991B1B',
  },
  refunded: {
    backgroundColor: '#F4F5F8',
    borderColor: '#E7E9EF',
    labelColor: '#5A5A5A',
    amountColor: '#171A20',
    metaColor: '#171A20',
  },
}

const drawerActionButtonClass =
  'h-10 rounded-xl border-[#e7e9ef] bg-white text-sm font-medium text-[#171a20] hover:bg-[#f8f9fb]'

function getPaymentStatusDate(payment: {
  status: PaymentStatus
  paid_date: string | null
  failed_at?: string | null
  refunded_at?: string | null
}): string | null {
  switch (payment.status) {
    case 'paid':
      return payment.paid_date
    case 'failed':
      return payment.failed_at ?? null
    case 'refunded':
      return payment.refunded_at ?? null
    default:
      return null
  }
}

function DetailSection({
  title,
  titleClassName,
  children,
}: {
  title: string
  titleClassName?: string
  children: ReactNode
}) {
  return (
    <section className='rounded-2xl border border-[#e7e9ef] bg-background shadow-[0_1px_3px_rgba(23,26,32,0.04)]'>
      <div className='overflow-hidden rounded-t-2xl'>
        <div className='border-l-4 border-primary px-6 mt-4'>
          <h3
            className={cn(
              'font-heading text-base font-semibold leading-[1.2] text-[#171a20]',
              titleClassName
            )}
          >
            {title}
          </h3>
        </div>
      </div>
      <div className='space-y-2 px-6 pb-6 pt-4'>{children}</div>
    </section>
  )
}

function DetailRow({
  label,
  value,
  labelClassName,
  valueClassName,
  className,
}: {
  label: string
  value: ReactNode
  labelClassName?: string
  valueClassName?: string
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 border-b border-[#e7e9ef] pb-2 last:border-b-0',
        className
      )}
    >
      <span
        className={cn(
          'text-sm font-medium leading-5 text-[#3d434e]',
          labelClassName
        )}
      >
        {label}
      </span>
      <div
        className={cn(
          'min-w-0 text-right text-sm font-medium leading-5 text-[#171a20]',
          valueClassName
        )}
      >
        {value}
      </div>
    </div>
  )
}

type Props = {
  paymentId: string
  autoOpenStatus?: boolean
  presentation?: 'default' | 'intervention'
  backActionLabel?: string
  onClose: () => void
  onBack?: () => void
}

export function PaymentDetailDrawerContent({
  paymentId,
  autoOpenStatus = false,
  presentation = 'default',
  backActionLabel,
  onClose,
  onBack,
}: Props) {
  const { t } = useTranslation('payments')
  const navigate = useNavigate()
  const { canEdit, canViewPath } = usePermissions()
  const qc = useQueryClient()
  const { data, isLoading, isError } = usePayment(paymentId)
  const proofInputRef = useRef<HTMLInputElement>(null)

  const [statusOpen, setStatusOpen] = useState(autoOpenStatus)
  const [markingPaid, setMarkingPaid] = useState(false)
  const [proofError, setProofError] = useState<string | null>(null)
  const [uploadingProof, setUploadingProof] = useState(false)
  const [proofDragActive, setProofDragActive] = useState(false)
  const paymentData = data?.data ?? null
  useBreadcrumbLabel(
    paymentData
      ? (paymentData.reference ?? paymentData.id.slice(0, 8))
      : undefined
  )
  const updatePaymentStatus = useUpdatePaymentStatus(paymentId, {
    jobId: paymentData?.job_id,
  })
  const { data: linkedJobData } = useJob(
    presentation === 'intervention' && paymentData?.job_id
      ? paymentData.job_id
      : undefined
  )
  const proofItems = useMemo<AttachmentRef[]>(() => {
    if (!paymentData) return []

    if (paymentData.proof_files?.length) {
      return paymentData.proof_files.map(file => ({
        id: file.id,
        name: file.name,
        size: file.size,
        mime_type: file.mime_type,
        url: file.url,
        created_at: file.created_at ?? paymentData.updated_at,
        source: 'upload' as const,
      }))
    }

    if (!paymentData.proof_url) return []

    return [
      {
        id: paymentData.proof_file_id ?? `${paymentData.id}-proof`,
        name: paymentData.proof_file_name ?? t('detail.proofOfPayment'),
        size: paymentData.proof_file_size ?? null,
        url: paymentData.proof_url,
        created_at: paymentData.updated_at,
        source: 'upload' as const,
      },
    ]
  }, [paymentData, t])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['payments', 'detail', paymentId] })
    qc.invalidateQueries({ queryKey: ['payments', 'list'] })
  }

  if (isLoading) {
    return (
      <div className='flex h-full items-center justify-center bg-[#F4F5F8] p-4 text-sm text-muted-foreground'>
        {t('detail.loading')}
      </div>
    )
  }

  if (isError || !data?.data) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-3 bg-[#F4F5F8] p-4 text-center'>
        <p className='text-sm text-destructive'>{t('detail.loadError')}</p>
        <Button variant='outline' onClick={onClose}>
          {t('common:actions.close')}
        </Button>
      </div>
    )
  }

  const payment = data.data
  const canEditPayment = canEdit('payments')
  const clientName =
    payment.client?.name ?? payment.client_name ?? t('detail.viewClient')
  const canViewInvoice =
    !!payment.invoice_id && canViewPath(`/invoices/${payment.invoice_id}`)
  const primaryProof = proofItems[0] ?? null
  const hasViewableProof = !!primaryProof?.url
  const isPaid = payment.status === 'paid'
  const isInterventionPayment = presentation === 'intervention'
  const useDetailTypography = !isInterventionPayment
  const linkedJob = linkedJobData?.data ?? null
  const interventionService =
    linkedJob?.service_type ?? t('form.interventionServiceFallback')
  const interventionClientType =
    linkedJob?.client?.type === 'individual'
      ? t('form.interventionClientIndividual')
      : t('form.interventionClientBusiness')
  const interventionSubtitle = [
    linkedJob?.reference ?? payment.job_reference,
    linkedJob ? interventionClientType : null,
    linkedJob?.start_time && linkedJob?.end_time
      ? `${linkedJob.start_time} - ${linkedJob.end_time}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')
  const amountTheme = PAYMENT_AMOUNT_THEMES[payment.status]
  const statusDate = getPaymentStatusDate(payment)
  const paymentAmountLabel = t(
    payment.status === 'pending'
      ? 'detail.pendingPaymentAmount'
      : 'detail.paymentAmount'
  )
  const amountStatusDateLabel = statusDate
    ? (() => {
        switch (payment.status) {
          case 'paid':
            return `✓ ${t('detail.receivedOn', {
              date: formatDate(statusDate),
            })}`
          case 'failed':
            return `✕ ${t('detail.failedOn', {
              date: formatDate(statusDate),
            })}`
          case 'refunded':
            return `✕ ${t('detail.refundedOn', {
              date: formatDate(statusDate),
            })}`
          default:
            return null
        }
      })()
    : null

  const openProof = () => {
    if (!primaryProof?.url) return
    window.open(primaryProof.url, '_blank', 'noopener,noreferrer')
  }

  const uploadProof = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0 || uploadingProof) return

    const validFiles: File[] = []
    let fileError: string | null = null
    for (const file of Array.from(files)) {
      if (!isPaymentProofFileSupported(file)) {
        fileError = t('uploadProofDialog.unsupportedFormat')
        continue
      }
      if (isPaymentProofFileTooLarge(file)) {
        fileError = t('uploadProofDialog.fileTooLarge')
        continue
      }
      validFiles.push(file)
    }

    setProofError(fileError)
    if (validFiles.length === 0) return
    setUploadingProof(true)
    try {
      await paymentsApi.uploadProof(payment.id, validFiles)
      toast({ variant: 'success', title: t('uploadProofDialog.uploaded') })
      invalidate()
    } catch (error) {
      setProofError(
        getApiErrorMessage(error, t('uploadProofDialog.uploadError'))
      )
    } finally {
      setUploadingProof(false)
    }
  }

  const renameProof = async (item: AttachmentRef, name: string) => {
    if (!item.id) return
    await paymentsApi.renameProof(payment.id, item.id, name)
    toast({ variant: 'success', title: t('detail.renameProofSuccess') })
    invalidate()
  }

  const markCollectedToday = async () => {
    setMarkingPaid(true)
    try {
      await updatePaymentStatus.mutateAsync({ status: 'paid' })
      toast({ variant: 'success', title: t('updateStatusDialog.updated') })
      invalidate()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('updateStatusDialog.updateError')),
      })
    } finally {
      setMarkingPaid(false)
    }
  }

  return (
    <div className='flex h-full min-h-0 flex-col bg-[#F4F5F8]'>
      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto',
          isInterventionPayment ? 'p-6' : 'p-4'
        )}
      >
        <div className='flex flex-col gap-4'>
          {backActionLabel && onBack ? (
            <div>
              <Button
                type='button'
                variant='ghost'
                className='h-8 rounded-md bg-white px-2 text-xs font-medium text-[#171a20] shadow-sm hover:bg-white'
                onClick={onBack}
              >
                <ArrowLeft className='mr-1 h-3.5 w-3.5' />
                {backActionLabel}
              </Button>
            </div>
          ) : null}

          {isInterventionPayment ? (
            <section className='overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_3px_rgba(23,26,32,0.04)]'>
              <header className='px-5 py-4'>
                <div className='mb-2 flex flex-wrap items-center gap-2'>
                  <span className='rounded-lg bg-[#D1FAE5] px-2 py-1 text-xs font-normal leading-none text-[#065F46]'>
                    {t('form.paymentBadge')}
                  </span>
                  <span className='inline-flex items-center gap-1 rounded-lg bg-[#eef2f7] px-2 py-1 text-xs font-normal leading-none text-[#171a20]'>
                    <Bug className='size-3' strokeWidth={1.8} />
                    {interventionService}
                  </span>
                </div>
                <h2 className='font-heading text-xl font-semibold leading-[1.2] text-[#171a20]'>
                  {t('form.interventionPaymentTitle')}
                </h2>
                <p className='mt-1 text-xs font-medium leading-[1.35] text-[#3d434e]'>
                  {interventionSubtitle || payment.id.slice(0, 8)}
                </p>
              </header>

              <div className='border-t border-[#e7e9ef] px-6 py-4'>
                <h3 className='mb-3 border-l-4 border-primary pl-2 font-heading text-base font-semibold uppercase leading-[1.2] text-[#171a20]'>
                  {t('form.information')}
                </h3>
                <div className='overflow-hidden rounded-2xl bg-[#f4f5f8] px-4'>
                  <DetailRow
                    label={t('form.client')}
                    value={clientName}
                    className='py-3'
                  />
                  <DetailRow
                    label={t('form.linkedJob')}
                    value={payment.job_reference ?? '--'}
                    className='py-3'
                  />
                  <DetailRow
                    label={t('form.linkedInvoice')}
                    value={
                      payment.invoice
                        ? `${payment.invoice.number ?? t('form.invoiceFallback')} - ${formatCurrency(payment.invoice.total, 'EUR', 2)}`
                        : (payment.invoice_number ?? '--')
                    }
                    className='py-3'
                  />
                </div>
              </div>
            </section>
          ) : (
            <section className='rounded-2xl border border-[#e7e9ef] bg-background px-4 py-4 shadow-[0_1px_3px_rgba(23,26,32,0.04)]'>
              <div className='flex flex-col gap-2'>
                <div>
                  <PaymentStatusBadge status={payment.status} />
                </div>
                <div>
                  <p
                    className={cn(
                      'font-heading text-[20px] font-semibold leading-[1.2] text-[#171a20]',
                      useDetailTypography && 'text-base'
                    )}
                  >
                    {clientName}
                  </p>
                  <div className='mt-2'>
                    <p
                      className={cn(
                        'text-sm font-normal leading-5 text-[#3d434e]',
                        useDetailTypography && 'text-xs leading-4'
                      )}
                    >
                      {payment.reference ?? payment.id.slice(0, 8)}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section
            className='rounded-2xl px-4 py-5 text-center shadow-[0_1px_3px_rgba(23,26,32,0.04)]'
            style={{
              border: `1px solid ${amountTheme.borderColor}`,
              backgroundColor: amountTheme.backgroundColor,
            }}
          >
            <p
              className={cn(
                'font-heading text-sm font-semibold leading-[1.2]',
                useDetailTypography && 'font-sans text-xs font-medium leading-4'
              )}
              style={{ color: amountTheme.labelColor }}
            >
              {paymentAmountLabel}
            </p>
            <p
              className='mt-2 font-heading text-[24px] font-semibold leading-[1.2] tracking-[-0.02em]'
              style={{ color: amountTheme.amountColor }}
            >
              {formatCurrency(payment.amount, 'EUR', 2)}
            </p>
            {amountStatusDateLabel && (
              <p
                className='mt-1 text-xs font-medium leading-4'
                style={{ color: amountTheme.metaColor }}
              >
                {amountStatusDateLabel}
              </p>
            )}
          </section>

          <DetailSection
            title={t('detail.details')}
            titleClassName={useDetailTypography ? 'text-sm' : undefined}
          >
            <input
              ref={proofInputRef}
              type='file'
              multiple
              accept={PAYMENT_PROOF_ACCEPTED_EXT}
              className='hidden'
              disabled={uploadingProof}
              onChange={event => {
                const files = Array.from(event.target.files ?? [])
                event.target.value = ''
                if (files.length > 0) void uploadProof(files)
              }}
            />
            <DetailRow
              label={t('detail.paymentMethod')}
              labelClassName={useDetailTypography ? 'leading-[1.4]' : undefined}
              value={
                PAYMENT_METHOD_LABELS[payment.method as PaymentMethod] ??
                payment.method
              }
              valueClassName={useDetailTypography ? 'leading-[1.4]' : undefined}
            />
            <DetailRow
              label={t('detail.status')}
              labelClassName={useDetailTypography ? 'leading-[1.4]' : undefined}
              value={
                <PaymentStatusBadge
                  status={payment.status}
                  className={
                    useDetailTypography ? 'text-sm leading-[1.4]' : undefined
                  }
                />
              }
            />
            <DetailRow
              label={t('detail.collectionDate')}
              value={formatDate(payment.paid_date)}
              labelClassName={useDetailTypography ? 'leading-[1.4]' : undefined}
              valueClassName={useDetailTypography ? 'leading-[1.4]' : undefined}
            />
            <DetailRow
              label={t('detail.linkedInvoice')}
              value={payment.invoice?.number ?? '--'}
              labelClassName={useDetailTypography ? 'leading-[1.4]' : undefined}
              valueClassName={useDetailTypography ? 'leading-[1.4]' : undefined}
            />
            <DetailRow
              label={t('detail.notes')}
              value={payment.note?.trim() ? payment.note : '--'}
              labelClassName={useDetailTypography ? 'leading-[1.4]' : undefined}
              valueClassName={cn(
                'max-w-[220px] whitespace-pre-wrap text-sm font-medium leading-5 text-[#171a20]',
                useDetailTypography && 'leading-[1.4]'
              )}
            />
            <DetailRow
              label={t('detail.proofOfPayment')}
              value={
                <div className='space-y-3'>
                  {proofItems.length > 0 ? (
                    <AttachmentPreviewGrid
                      items={proofItems}
                      removeLabel=''
                      onRename={canEditPayment ? renameProof : undefined}
                      onItemClick={item => {
                        if (item.url) {
                          window.open(item.url, '_blank', 'noopener,noreferrer')
                        }
                      }}
                      itemClickLabel={t('detail.viewProof')}
                      typeBadgeClassName={
                        useDetailTypography ? 'font-medium' : undefined
                      }
                      metadataClassName={
                        useDetailTypography
                          ? 'font-normal leading-4'
                          : undefined
                      }
                    />
                  ) : (
                    <span></span>
                  )}
                  {hasViewableProof && isPaid && (
                    <button
                      type='button'
                      className='inline-flex items-center rounded-lg bg-[#edfdf3] px-2.5 py-1.5 text-xs font-semibold text-[#0b7a5c] transition-colors hover:bg-[#dff8ea]'
                      onClick={openProof}
                    >
                      <Paperclip className='mr-1.5 h-3.5 w-3.5' />
                      {t('detail.viewProof')}
                    </button>
                  )}
                  {canEditPayment && (
                    <FileUploadDropzone
                      onClick={() => proofInputRef.current?.click()}
                      disabled={uploadingProof}
                      loading={uploadingProof}
                      loadingText={t('shared:attachFiles.uploading')}
                      dragActive={proofDragActive}
                      onDragOver={event => {
                        event.preventDefault()
                        setProofDragActive(true)
                      }}
                      onDragLeave={event => {
                        event.preventDefault()
                        setProofDragActive(false)
                      }}
                      onDrop={event => {
                        event.preventDefault()
                        setProofDragActive(false)
                        if (
                          !uploadingProof &&
                          event.dataTransfer.files.length > 0
                        ) {
                          void uploadProof(event.dataTransfer.files)
                        }
                      }}
                      primaryText={t('form.proofDropzonePrimary')}
                      secondaryText={t('form.proofDropzoneFormats')}
                      hasError={!!proofError}
                      className={
                        useDetailTypography
                          ? '[&>span:last-child>span]:font-normal [&>span:last-child>span]:leading-[1.4]'
                          : undefined
                      }
                    />
                  )}
                  {proofError && (
                    <p className='text-xs text-destructive'>{proofError}</p>
                  )}
                </div>
              }
              className='flex-col items-start gap-3'
              labelClassName={useDetailTypography ? 'leading-[1.4]' : undefined}
              valueClassName={cn(
                'w-full text-left text-sm font-medium leading-5 text-[#171a20]',
                useDetailTypography && 'leading-[1.4]'
              )}
            />
            {payment.status === 'failed' && payment.failure_reason && (
              <DetailRow
                label={t('detail.failureReason')}
                value={PAYMENT_FAILURE_REASON_LABELS[payment.failure_reason]}
                labelClassName={
                  useDetailTypography ? 'leading-[1.4]' : undefined
                }
                valueClassName={cn(
                  'text-destructive',
                  useDetailTypography && 'leading-[1.4]'
                )}
              />
            )}
          </DetailSection>

          <div className='flex flex-col gap-2'>
            {canEditPayment &&
              (payment.status === 'pending' ? (
                <Button
                  type='button'
                  className={cn(
                    'h-10 rounded-xl text-base font-medium text-white',
                    useDetailTypography && 'text-sm leading-none'
                  )}
                  disabled={markingPaid}
                  onClick={markCollectedToday}
                >
                  {t('detail.markCollectedToday')}
                </Button>
              ) : (
                <Button
                  type='button'
                  variant='outline'
                  className={cn(
                    drawerActionButtonClass,
                    useDetailTypography && 'leading-none'
                  )}
                  onClick={() => setStatusOpen(true)}
                >
                  <RefreshCw className='mr-2 h-3.5 w-3.5' />
                  {t('detail.updateStatus')}
                </Button>
              ))}

            {(canEditPayment || canViewInvoice) && (
              <div
                className={cn(
                  'grid gap-2',
                  canEditPayment && canViewInvoice
                    ? 'grid-cols-2'
                    : 'grid-cols-1'
                )}
              >
                {canEditPayment && (
                  <Button
                    type='button'
                    variant='outline'
                    className={cn(
                      drawerActionButtonClass,
                      useDetailTypography && 'leading-none'
                    )}
                    disabled={uploadingProof}
                    onClick={() => proofInputRef.current?.click()}
                  >
                    <Paperclip className='mr-2 h-3.5 w-3.5' />
                    {t('detail.proofButton')}
                  </Button>
                )}
                <Button
                  type='button'
                  variant='outline'
                  className={cn(
                    drawerActionButtonClass,
                    useDetailTypography && 'leading-none'
                  )}
                  disabled={!payment.invoice_id || !canViewInvoice}
                  onClick={() => {
                    if (payment.invoice_id && canViewInvoice) {
                      navigate(`/invoices/${payment.invoice_id}`)
                    }
                  }}
                >
                  <ExternalLink className='mr-2 h-3.5 w-3.5' />
                  {t('detail.viewInvoiceButton')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {canEditPayment && (
        <UpdatePaymentStatusDialog
          open={statusOpen}
          onOpenChange={setStatusOpen}
          paymentId={payment.id}
          jobId={payment.job_id}
          currentStatus={payment.status}
          currentFailureReason={payment.failure_reason}
          onUpdated={invalidate}
        />
      )}
    </div>
  )
}

export default PaymentDetailDrawerContent
